import * as React from 'react';

export function useSidebarSwipeGesture({
  isDesktop,
  mobileSidebarOpen,
  setMobileSidebarOpen,
  activationEdgePx = 24,
  swipeThresholdPx = 70,
  cancelVerticalThresholdPx = 60,
}: {
  isDesktop: boolean;
  mobileSidebarOpen: boolean;
  setMobileSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activationEdgePx?: number;
  swipeThresholdPx?: number;
  cancelVerticalThresholdPx?: number;
}): void {
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (isDesktop) return undefined;

    let startX = 0;
    let startY = 0;
    let isTracking = false;

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (!touch) return;
      startX = touch.clientX;
      startY = touch.clientY;
      isTracking = mobileSidebarOpen || (!mobileSidebarOpen && touch.clientX <= activationEdgePx);
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!isTracking) return;
      const touch = event.touches[0];
      if (!touch) return;
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;
      if (Math.abs(deltaY) > cancelVerticalThresholdPx) {
        isTracking = false;
        return;
      }

      if (!mobileSidebarOpen && deltaX > swipeThresholdPx) {
        setMobileSidebarOpen(true);
        isTracking = false;
      }

      if (mobileSidebarOpen && deltaX < -swipeThresholdPx) {
        setMobileSidebarOpen(false);
        isTracking = false;
      }
    };

    const onTouchEnd = () => {
      isTracking = false;
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [
    activationEdgePx,
    cancelVerticalThresholdPx,
    isDesktop,
    mobileSidebarOpen,
    setMobileSidebarOpen,
    swipeThresholdPx,
  ]);
}
